import { useState, useEffect, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import axios from 'axios';
import { pageCache } from '../utils/pageCache';
import { DollarSign } from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { fmt, fmtK, fmtCents, fmtCount, fmtPct } from '../utils/formatters';
import { formatRange } from '../utils/dataEnds';
import LoadingScreen from '../components/LoadingScreen';
import DataEndsNote from '../components/DataEndsNote';
import Change from '../components/Change';

// total: a period total in whole dollars · cents: one average price · pct: a share · count: a count
type KpiFormat = 'total' | 'cents' | 'pct' | 'count';
const FORMAT: Record<KpiFormat, (v: number) => string> = { total: fmt, cents: fmtCents, pct: fmtPct, count: fmtCount };

function KpiCard({ label, value, change, format = 'total', changeUnit = '%', good = 'up', chip }: {
  label: string; value: number; change: number | null | undefined; format?: KpiFormat; changeUnit?: '%' | 'pts'; good?: 'up' | 'down' | 'neither'; chip?: ReactNode;
}) {
  return (
    <div className="flex flex-col bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card hover:-translate-y-1 hover:shadow-[0_8px_24px_rgba(74,45,94,0.08)] transition-all duration-300">
      <div className="flex flex-wrap items-center gap-y-1 mb-1">
        <span className="text-xs font-sans font-medium text-[#6B6670] uppercase tracking-wide">{label}</span>
        {chip}
      </div>
      {/* Pinned to the bottom so figures line up when a label wraps. */}
      <div className="mt-auto">
        <div className="text-2xl font-sans font-bold text-[#2D2A2E] tabular-nums">{FORMAT[format](value)}</div>
        {/* null: this period and the last count different units (orders vs items), so there is no honest change to show. */}
        {change != null && (
          <div className="flex flex-wrap items-center gap-1 mt-1">
            <Change value={change} unit={changeUnit} good={good} />
            <span className="text-[#6B6670] text-xs font-sans ml-1">vs prior period</span>
          </div>
        )}
      </div>
    </div>
  );
}

// The Profit chip's look, as a plain label: Cost and Margin rest on the same estimate.
const CHIP = 'ml-2 inline-flex items-center rounded-badge bg-[#F0EBF5] px-2 py-0.5 font-sans text-[11px] font-medium text-[#4A2D5E]';

// Forest for a profit, dusty rose for a loss — judged on the rounded figure, so "$0" is never red.
const profitTone = (v: number) => (Math.round(v) < 0 ? 'text-[#B54A4A]' : 'text-[#2E6B4F]');

export default function FinancialsPage() {
  const [data, setData] = useState<any>(() => pageCache.get('/api/financials?period=month') ?? null);
  const [loading, setLoading] = useState(() => !pageCache.has('/api/financials?period=month'));
  const [period, setPeriod] = useState('month');
  const [scenario, setScenario] = useState({ priceChange: 0, costChange: 0 });
  const [showBasis, setShowBasis] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    setLoading(true);
    const url = `/api/financials?period=${period}`;
    if (pageCache.has(url)) {
      setData(pageCache.get(url));
      setLoading(false);
    }
    axios.get(url)
      .then((res) => { pageCache.set(url, res.data); setData(res.data); })
      .catch(() => { if (!pageCache.has(url)) setData(null); })
      .finally(() => setLoading(false));
  }, [period]);

  const hasData = data && data.kpis && data.kpis.revenue.value > 0;

  const scenarioRevenue = hasData ? data.scenario_base.current_revenue * (1 + scenario.priceChange / 100) : 0;
  const scenarioCost = hasData ? data.scenario_base.current_cost * (1 + scenario.costChange / 100) : 0;
  const scenarioProfit = scenarioRevenue - scenarioCost;
  const scenarioMargin = scenarioRevenue > 0 ? (scenarioProfit / scenarioRevenue * 100) : 0;

  const basis = hasData ? data.cost_basis : null;
  // An older backend sends no orders block, or one without a label.
  const orders = hasData ? data.kpis.orders : null;
  const avgOrder = hasData ? data.kpis.avg_order : null;
  const estimatedTag = basis?.estimated ? <span className={CHIP}>Estimated</span> : undefined;

  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease: 'easeOut' }}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-serif text-2xl text-[#2D2A2E]">Financial Health</h1>
          <DataEndsNote dataEnds={data?.data_ends} period={period} />
          {hasData && data.provenance && (
            data.provenance.verified_products > 0 ? (
              <span className="inline-flex items-center gap-1.5 mt-2 bg-[#2E6B4F]/10 text-[#2E6B4F] rounded-full px-2.5 py-1 font-sans text-xs font-medium">
                {fmtPct(data.provenance.coverage_pct)} of revenue uses invoice-verified costs · {fmtCount(data.provenance.verified_products)} products
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 mt-2 bg-[#FAF9F7] border border-[#E6E2DE] text-[#6B6670] rounded-full px-2.5 py-1 font-sans text-xs">
                Costs are estimated from your COGS % — upload a supplier invoice to verify them
              </span>
            )
          )}
        </div>
        <select value={period} onChange={(e) => setPeriod(e.target.value)} className="bg-white border border-[#E6E2DE] focus:ring-2 focus:ring-[#4A2D5E]/20 focus:border-[#4A2D5E] rounded-[8px] h-10 font-sans text-sm px-3 outline-none w-44">
          <option value="week">This Week</option>
          <option value="month">This Month</option>
          <option value="quarter">Last 90 Days</option>
        </select>
      </div>

      {loading ? (
        <LoadingScreen fullScreen={false} />
      ) : !hasData ? (
        <div className="flex flex-col items-center justify-center py-24">
          <DollarSign size={48} strokeWidth={1.5} className="text-[#4A2D5E]/30 mb-4" />
          <h3 className="font-serif text-xl text-[#2D2A2E] mb-2">No financial data yet</h3>
          <p className="font-sans text-sm text-[#6B6670] mb-6 text-center max-w-sm">Upload your transaction data to see revenue, profit, margin trends, and use the what-if scenario planner.</p>
          <button onClick={() => navigate('/settings/data')} className="bg-[#2E6B4F] text-white hover:bg-[#245A41] rounded-[8px] shadow-sm font-sans font-medium px-6 py-2.5 transition-colors">Upload Data</button>
        </div>
      ) : (
        <>
          <div className="mb-8">
            {/* Dense flow: the explanation (col-span-full) lands on the row right under the Profit card at every width, the next card backfilling beside Profit; at xl it follows the whole row. */}
            <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 grid-flow-row-dense gap-4">
              <KpiCard label="Revenue" value={data.kpis.revenue.value} change={data.kpis.revenue.change} />
              {/* Fully estimated, cost is revenue times the food-cost %: its change only repeats Revenue's, so it is not judged. */}
              <KpiCard label="Cost" value={data.kpis.cost.value} change={data.kpis.cost.change}
                good={basis?.estimated_share_pct === 100 ? 'neither' : 'down'} chip={estimatedTag} />
              <KpiCard label="Gross profit (after COGS)" value={data.kpis.profit.value} change={data.kpis.profit.change}
                chip={basis?.estimated ? (
                  <button type="button" aria-expanded={showBasis} aria-controls="cost-basis-note" onClick={() => setShowBasis((s) => !s)}
                    className={`${CHIP} focus:outline-none focus-visible:ring-2 focus-visible:ring-[#4A2D5E] focus-visible:ring-offset-1`}>
                    Estimated
                  </button>
                ) : undefined} />
              {basis?.estimated && (
                <p id="cost-basis-note" hidden={!showBasis} className="col-span-full xl:order-last font-sans text-xs text-[#6B6670]">
                  Cost uses your {fmtPct(basis.cogs_percentage)} food cost on {fmtPct(basis.estimated_share_pct)} of sales lines. Add supplier invoices under Costs to replace it.
                </p>
              )}
              <KpiCard label="Margin" value={data.kpis.margin.value} change={data.kpis.margin.change} format="pct" changeUnit="pts" chip={estimatedTag} />
              {orders && <KpiCard label={orders.label || 'Sales'} value={orders.value} change={orders.change} format="count" />}
              {avgOrder && <KpiCard label={avgOrder.label || 'Avg Order'} value={avgOrder.value} change={avgOrder.change} format="cents" />}
            </div>
            {data.window && orders && (
              <p className="mt-3 font-sans text-xs text-[#6B6670] tabular-nums">
                {fmtCents(data.kpis.revenue.value)} from {fmtCount(orders.value)} {(orders.label || 'sales').toLowerCase()}, {formatRange(data.window.start, data.window.end)}.
              </p>
            )}
          </div>

          {/* Revenue vs Cost vs Profit chart */}
          <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card mb-8">
            <h3 className="font-serif text-lg text-[#2D2A2E] mb-4">Revenue, Cost & Profit Trend</h3>
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={data.trend}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4A2D5E" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#4A2D5E" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2E6B4F" stopOpacity={0.1} />
                    <stop offset="95%" stopColor="#2E6B4F" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E2DE" />
                <XAxis dataKey="date" tick={{ fill: '#6B6670', fontSize: 12, fontFamily: 'DM Sans' }} tickFormatter={(v) => v.slice(5)} />
                <YAxis tick={{ fill: '#6B6670', fontSize: 12, fontFamily: 'DM Sans' }} tickFormatter={(v) => fmtK(v)} />
                <Tooltip contentStyle={{ borderRadius: '10px', border: '1px solid #E6E2DE', fontFamily: 'DM Sans', fontSize: 12 }} formatter={(value: number, name: string) => [fmt(value), name.charAt(0).toUpperCase() + name.slice(1)]} labelFormatter={(l) => `Date: ${l}`} />
                <Legend />
                <Area type="monotone" dataKey="revenue" stroke="#4A2D5E" strokeWidth={2} fill="url(#revGrad)" />
                <Area type="monotone" dataKey="cost" stroke="#B54A4A" strokeWidth={2} fill="none" />
                <Area type="monotone" dataKey="profit" stroke="#2E6B4F" strokeWidth={2} fill="url(#profitGrad)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          {/* Category breakdown + What-if side by side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Category P&L */}
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] overflow-hidden">
              <div className="p-6 border-b border-[#E6E2DE]">
                <h3 className="font-serif text-lg text-[#2D2A2E]">Category Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#FAF9F7] border-b border-[#E6E2DE]">
                      {['Category', 'Revenue', 'Cost', 'Profit', 'Margin'].map((h) => (
                        <th key={h} className={`p-4 text-xs font-medium text-[#6B6670] uppercase tracking-wide font-sans ${h === 'Category' ? 'text-left' : 'text-right'}`}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.categories.map((c: any) => (
                      <tr key={c.category} className="border-b border-[#E6E2DE] last:border-0 hover:bg-[#FAF9F7] transition-colors">
                        <td className="p-4 text-sm font-sans text-[#2D2A2E] font-medium">{c.category}</td>
                        <td className="p-4 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmt(c.revenue)}</td>
                        <td className="p-4 text-sm font-sans text-[#6B6670] text-right tabular-nums">{fmt(c.cost)}</td>
                        <td className={`p-4 text-sm font-sans ${profitTone(c.profit)} text-right tabular-nums`}>{fmt(c.profit)}</td>
                        <td className="p-4 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmtPct(c.margin)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {/* The period's true totals (the KPIs), not a sum of the rows: the table shows at most 20 categories. */}
                    <tr className="border-t border-[#E6E2DE] font-medium">
                      <td className="p-4 text-sm font-sans text-[#2D2A2E]">Total</td>
                      <td className="p-4 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmt(data.kpis.revenue.value)}</td>
                      <td className="p-4 text-sm font-sans text-[#6B6670] text-right tabular-nums">{fmt(data.kpis.cost.value)}</td>
                      <td className={`p-4 text-sm font-sans ${profitTone(data.kpis.profit.value)} text-right tabular-nums`}>{fmt(data.kpis.profit.value)}</td>
                      <td className="p-4 text-sm font-sans text-[#2D2A2E] text-right tabular-nums">{fmtPct(data.kpis.margin.value)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </div>

            {/* What-if Planner */}
            <div className="bg-white border border-[#E6E2DE] rounded-[14px] p-6 shadow-card">
              <h3 className="font-serif text-lg text-[#2D2A2E] mb-4">What-If Scenario Planner</h3>
              <div className="space-y-5">
                <div>
                  <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-2">
                    Price change: <Change value={scenario.priceChange} size="sm" />
                  </label>
                  <input type="range" min={-30} max={30} value={scenario.priceChange} onChange={(e) => setScenario({ ...scenario, priceChange: Number(e.target.value) })} className="w-full accent-[#4A2D5E]" />
                </div>
                <div>
                  <label className="block text-sm font-sans font-medium text-[#2D2A2E] mb-2">
                    Cost change: <Change value={scenario.costChange} size="sm" good="down" />
                  </label>
                  <input type="range" min={-30} max={30} value={scenario.costChange} onChange={(e) => setScenario({ ...scenario, costChange: Number(e.target.value) })} className="w-full accent-[#4A2D5E]" />
                </div>
                <div className="bg-[#FAF9F7] rounded-[8px] p-4 space-y-2">
                  <div className="flex justify-between text-sm font-sans">
                    <span className="text-[#6B6670]">Projected Revenue</span>
                    <span className="text-[#2D2A2E] font-medium tabular-nums">{fmt(scenarioRevenue)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-sans">
                    <span className="text-[#6B6670]">Projected Cost</span>
                    <span className="text-[#2D2A2E] font-medium tabular-nums">{fmt(scenarioCost)}</span>
                  </div>
                  <div className="border-t border-[#E6E2DE] pt-2 flex justify-between text-sm font-sans">
                    <span className="text-[#6B6670]">Projected Profit</span>
                    <span className={`font-medium tabular-nums ${scenarioProfit >= 0 ? 'text-[#2E6B4F]' : 'text-[#B54A4A]'}`}>{fmt(scenarioProfit)}</span>
                  </div>
                  <div className="flex justify-between text-sm font-sans">
                    <span className="text-[#6B6670]">Projected Margin</span>
                    <span className={`font-medium tabular-nums ${scenarioMargin >= 0 ? 'text-[#2E6B4F]' : 'text-[#B54A4A]'}`}>{fmtPct(scenarioMargin)}</span>
                  </div>
                  <p className="pt-1 text-[11px] font-sans text-[#7C7589]">
                    Estimate assumes unit volume and product mix stay constant. Actual results will vary with demand.
                  </p>
                </div>
                <button
                  onClick={() => setScenario({ priceChange: 0, costChange: 0 })}
                  className="w-full bg-[#FAF9F7] text-[#4A2D5E] border border-[#4A2D5E]/20 hover:bg-[#F2F0ED] rounded-[8px] font-sans text-sm py-2 transition-colors"
                >
                  Reset Scenario
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </motion.div>
  );
}
